import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import style from './NoticeableUSInvestors.module.css';
import useGurus from '../../hooks/useGurus';
import fetcher from '../../hooks/fetcher';

const NoticeableUSInvestors = ({ stock, hubGurus, price = 0 }) => {
  const { gurus } = useGurus(stock.id, hubGurus);
  const [openGuruId, setOpenGuruId] = useState(null);
  const [hoveredGuruId, setHoveredGuruId] = useState(null);
  const [detailsByGuru, setDetailsByGuru] = useState({});
  const [loadingGuruId, setLoadingGuruId] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const popoverContainerRef = useRef(null);
  const popoverRef = useRef(null);
  const triggerRefs = useRef({});

  useEffect(() => {
    const focusedGuruId = openGuruId || hoveredGuruId || null;

    window.dispatchEvent(
      new CustomEvent('stock-page-guru-focus', {
        detail: {
          stockId: stock.id,
          guruId: focusedGuruId
        }
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent('stock-page-guru-focus', {
          detail: {
            stockId: stock.id,
            guruId: null
          }
        })
      );
    };
  }, [openGuruId, hoveredGuruId, stock.id]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const activeTrigger = openGuruId ? triggerRefs.current[openGuruId] : null;
      const clickedInPopover = popoverRef.current?.contains(event.target);
      const clickedOnTrigger = activeTrigger?.contains(event.target);

      if (!clickedInPopover && !clickedOnTrigger && !popoverContainerRef.current?.contains(event.target)) {
        setOpenGuruId(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenGuruId(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openGuruId]);

  useEffect(() => {
    if (!openGuruId) {
      return undefined;
    }

    const updatePopoverPosition = () => {
      const activeTrigger = triggerRefs.current[openGuruId];
      if (!activeTrigger) {
        return;
      }

      const rect = activeTrigger.getBoundingClientRect();
      const viewportPadding = 8;
      const popoverWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(rect.left, viewportPadding),
        window.innerWidth - popoverWidth - viewportPadding
      );
      const top = Math.max(rect.bottom + 8, viewportPadding);

      setPopoverPosition({
        top,
        left
      });
    };

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [openGuruId]);

  if (!gurus || gurus.length === 0) {
    return null;
  }

  const handleTogglePopover = async (event, guruId) => {
    event.preventDefault();
    event.stopPropagation();

    if (openGuruId === guruId) {
      setOpenGuruId(null);
      setPopoverPosition(null);
      return;
    }

    setOpenGuruId(guruId);

    if (detailsByGuru[guruId]) {
      return;
    }

    setLoadingGuruId(guruId);
    try {
      const details = await fetcher(`/react/guru-position-details?stock_id=${stock.id}&guru_id=${guruId}`);
      setDetailsByGuru((prev) => ({ ...prev, [guruId]: details }));
    } catch (error) {
      setDetailsByGuru((prev) => ({ ...prev, [guruId]: { error: true } }));
    } finally {
      setLoadingGuruId(null);
    }
  };

  return (
    <div className={style.container} ref={popoverContainerRef}>
      {gurus.map((guru) => (
        <div key={guru.id} className={style.boxWrapper}>
          <button
            type="button"
            className={style.box}
            onClick={(event) => handleTogglePopover(event, guru.id)}
            onMouseEnter={() => setHoveredGuruId(guru.id)}
            onMouseLeave={() => setHoveredGuruId((current) => (current === guru.id ? null : current))}
            aria-expanded={openGuruId === guru.id}
            ref={(element) => {
              triggerRefs.current[guru.id] = element;
            }}
          >
            <span className={style.nameRow}>
              <span className={style.name}>{guru.name}</span>
              <span className={style.arrowBox}>
                <span className={`${style.arrow} ${openGuruId === guru.id ? style.arrowOpen : ''}`} />
              </span>
            </span>
            <span className={style.value}>
              ${formatNumber(guru.total_shares * price)}
            </span>
          </button>

          {openGuruId === guru.id && popoverPosition && createPortal(
            <div
              className={style.popover}
              ref={popoverRef}
              style={{ top: popoverPosition.top, left: popoverPosition.left }}
            >
              {loadingGuruId === guru.id && (
                <p className={style.loadingState}>Loading details...</p>
              )}

              {loadingGuruId !== guru.id && detailsByGuru[guru.id]?.error && (
                <p className={style.errorState}>Could not load investor details right now.</p>
              )}

              {loadingGuruId !== guru.id && detailsByGuru[guru.id] && !detailsByGuru[guru.id].error && (
                <>
                  <div className={style.popoverSection}>
                    <p className={style.popoverLabel}>Current holding value</p>
                    <p className={style.popoverValue}>${formatNumber(detailsByGuru[guru.id].holding_value)}</p>
                  </div>

                  <div className={style.popoverSection}>
                    <p className={style.popoverLabel}>Current shares</p>
                    <p className={style.popoverValue}>{formatShares(detailsByGuru[guru.id].shares_held)}</p>
                  </div>

                  <div className={style.popoverSection}>
                    <p className={style.popoverLabel}>Avg buy price</p>
                    <p className={style.popoverValue}>
                      {formatPrice(detailsByGuru[guru.id].avg_buy_price)}
                      <span className={`${style.priceChangeInline} ${priceChangeClass(detailsByGuru[guru.id].avg_buy_price, price)}`}>
                        {formatPriceChange(detailsByGuru[guru.id].avg_buy_price, price)}
                      </span>
                    </p>
                  </div>

                  <div className={style.popoverSection}>
                    <p className={style.popoverLabel}>Last trade</p>
                    <p className={style.lastTradeHeadline}>
                      <span className={lastTradeActionClass(detailsByGuru[guru.id].last_trade)}>
                        {lastTradeActionText(detailsByGuru[guru.id].last_trade)}
                      </span>
                      {` in ${detailsByGuru[guru.id].last_trade?.date || '-'}`}
                    </p>
                    <p className={style.lastTradeDetails}>
                      {formatLastTradeSummary(detailsByGuru[guru.id].last_trade)}
                    </p>
                  </div>

                  <a className={style.allTradesLink} href={detailsByGuru[guru.id].all_trades_path}>
                    View all {formatTradeCount(detailsByGuru[guru.id].trades_count)} trades
                  </a>
                </>
              )}
            </div>,
            document.body
          )}
        </div>
      ))}
    </div>
  );
};

export default NoticeableUSInvestors;

function formatNumber(num) {
  if (num == null || Number.isNaN(num)) {
    return '-';
  }
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num;
}

function formatShares(num) {
  if (num == null || Number.isNaN(num)) {
    return '-';
  }
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatPrice(num) {
  if (num == null || Number.isNaN(num)) {
    return '-';
  }
  return `$${Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTradePrice(lastTrade) {
  if (!lastTrade) {
    return '';
  }

  if (lastTrade.estimated_price) {
    return `est. price ${formatPrice(lastTrade.estimated_price)}`;
  }

  if (lastTrade.estimated_price_min && lastTrade.estimated_price_max) {
    return `est. range ${formatPrice(lastTrade.estimated_price_min)} - ${formatPrice(lastTrade.estimated_price_max)}`;
  }

  return '';
}

function formatPriceChange(avgBuyPrice, currentPrice) {
  if (!avgBuyPrice || !currentPrice) {
    return '';
  }

  const change = ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100;
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

function priceChangeClass(avgBuyPrice, currentPrice) {
  if (!avgBuyPrice || !currentPrice) {
    return '';
  }

  return currentPrice >= avgBuyPrice ? style.priceChangePositive : style.priceChangeNegative;
}

function lastTradeActionText(lastTrade) {
  if (!lastTrade) {
    return '-';
  }

  return lastTrade.share_change > 0 ? 'Bought' : 'Sold';
}

function lastTradeActionClass(lastTrade) {
  if (!lastTrade) {
    return '';
  }

  return lastTrade.share_change > 0 ? style.lastTradeBought : style.lastTradeSold;
}

function formatLastTradeSummary(lastTrade) {
  if (!lastTrade) {
    return '';
  }

  const details = lastTrade.change_description || '';
  const tradePrice = formatTradePrice(lastTrade);
  if (!tradePrice) {
    return details;
  }

  return `${details} (${tradePrice})`;
}

function formatTradeCount(count) {
  if (count == null || Number.isNaN(count)) {
    return '-';
  }

  return Number(count).toLocaleString();
}
